import { DataGridConfig } from 'components/DataGrid';
import OSInfo from 'components/OSInfo';
import history from 'history.js';
import { useAtom, useAtomValue } from 'jotai';
import callApi from 'lib/callApi';
import { truncateText } from 'lib/helper';
import { showFailureToast } from 'lib/ToastHelper';
import { Fragment, useEffect, useMemo, useState } from 'react';

import RowActions from './RowActions';
import {
  orderByAtom,
  sortOrderAtom,
  caListingDataAtom,
  checkedListAtom,
  activeCAColumnKeysAtom,
  paginationConfigAtom,
  DEFAULT_ACTIVE_CA_LISTING_COLUMNS,
  DEFAULT_AGED_OUT_CA_LISTING_COLUMNS,
  agedOutCAColumnKeysAtom,
  forceRerenderUIAtom,
  caDataQueryParamsAtom,
  isQuerystringFiltersInitializedAtom,
  useCAListFilterValue,
} from './state';
import AgingOutLastDate from '../../components/AgingOutLastDate/AgingOutLastDate';
import { getDrillDownUrl } from '../../helper';
import { appliedCAIdsAtom } from '../state';

export const useCADataConfig = () => {
  const dataQueryParams = useAtomValue(caDataQueryParamsAtom);
  const isQuerystringFiltersInitialized = useAtomValue(
    isQuerystringFiltersInitializedAtom,
  );
  const [listingData, setListingData] = useAtom(caListingDataAtom);
  const [paginationConfig, setPaginationConfig] = useAtom(paginationConfigAtom);
  const [orderBy, setOrderBy] = useAtom(orderByAtom);
  const [sortOrder, setSortOrder] = useAtom(sortOrderAtom);
  const appliedRowIds = useAtomValue(appliedCAIdsAtom);
  const [selectedRowIds, setSelectedRowIds] = useAtom(checkedListAtom);
  const [activeCAColumnKeys, updateActiveCAColumnKeys] = useAtom(
    activeCAColumnKeysAtom,
  );
  const [agedOutCAColumnKeys, updateAgedOutCAColumnKeys] = useAtom(
    agedOutCAColumnKeysAtom,
  );
  const forceRerenderUI = useAtomValue(forceRerenderUIAtom);
  const [loading, setLoading] = useState(false);
  const enableSelectionAcrossPages = true;

  useEffect(() => {
    setLoading(true);
    const fetchData = async () => {
      if (dataQueryParams === null) return;
      try {
        setListingData({ ...listingData, loading: true });
        const responseData = await callApi(
          `/cloud_agents/`,
          'GET',
          dataQueryParams,
        );
        setListingData({
          loading: false,
          items: responseData.data.results,
          count: responseData.data.count,
          insights: responseData.data.insights,
        });
      } catch (error) {
        const errorData = (error as any)?.data;
        const errorMessage = errorData
          ? `${Object.keys(errorData)[0]}: ${
              Object.values(errorData).flat()[0]
            }`
          : 'Failed to load data.';
        showFailureToast(errorMessage);
        setListingData({
          items: [],
          count: 0,
          loading: false,
          insights: {
            servers_count: 0,
            severity_breakdown: {
              urgent: 0,
              critical: 0,
              serious: 0,
              medium: 0,
              minimal: 0,
            },
            status_breakdown: {
              active: 0,
              aging: 0,
              deleted: 0,
            },
            total_vulnerabilities: 0,
            unique_os_count: 0,
            workstations_count: 0,
          },
        });
      } finally {
        setLoading(false);
      }
    };

    if (isQuerystringFiltersInitialized) {
      fetchData();
    }
  }, [dataQueryParams, isQuerystringFiltersInitialized]);

  const isCloudAgentAgingOut = (rowData) => {
    const checkedInDate = new Date(rowData['last_checked_in']);
    const now = new Date();
    const tenDaysAgo = new Date();
    tenDaysAgo.setDate(now.getDate() - 10);
    return checkedInDate < tenDaysAgo;
  };
  const selectedCloudAgentsFilter = useCAListFilterValue<
    'active' | 'aging' | 'deleted'
  >('filter');
  const isDeletedFilterSelected = () => {
    return selectedCloudAgentsFilter === 'deleted';
  };

  const getColumnsDisplayConfig = () => {
    if (selectedCloudAgentsFilter === 'deleted') {
      return {
        enabledColumnKeys: agedOutCAColumnKeys,
        defaultColumnKeys: DEFAULT_AGED_OUT_CA_LISTING_COLUMNS,
        keysToBeExcluded: [
          'total_vuln',
          'new_vuln',
          'last_vuln_scan',
          'last_checked_in',
        ],
        onColumnsDisplayChange: (updatedColumnKeys) => {
          updateAgedOutCAColumnKeys(updatedColumnKeys);
        },
      };
    } else {
      return {
        enabledColumnKeys: activeCAColumnKeys,
        defaultColumnKeys: DEFAULT_ACTIVE_CA_LISTING_COLUMNS,
        keysToBeExcluded: [
          'deleted_cloudagent__open_vuln_counts',
          'deleted_cloudagent__closed_vuln_counts',
          'deleted_cloudagent__deleted_at',
          'deleted_cloudagent__deleted_by',
        ],
        onColumnsDisplayChange: (updatedColumnKeys) => {
          updateActiveCAColumnKeys(updatedColumnKeys);
        },
      };
    }
  };

  const getGridConfig = (): DataGridConfig => {
    const config: DataGridConfig = {
      rowIdKey: 'id',

      columns: [
        {
          key: 'id',
          label: 'Id',
          sortable: true,
          headerTooltip: 'Row identifier in SP360 database',
        },
        {
          key: 'ca_id',
          label: 'Cloud Agent Id',
          sortable: true,
          orderBy: 'ca_id',
          headerTooltip: 'Cloud Agent Id at Qualys',
        },
        {
          key: 'agentkey__title',
          label: 'Agent Title',
          sortable: true,
          orderBy: 'agentkey__title',
          headerTooltip: 'Cloud Agent activation key title at Qualys',
          overrideContents: (rowData) => (
            <Fragment>
              {rowData['agentkey__title']
                ? rowData['agentkey__title'].split('-')[1].trim()
                : ''}
            </Fragment>
          ),
          tooltip: (rowData) => (
            <Fragment>
              {rowData['agentkey__title']
                ? rowData['agentkey__title'].split('-')[1].trim()
                : ''}
            </Fragment>
          ),
          headerCellStyle: { width: 'auto' },
          rowCellStyle: { width: 'auto', textDecoration: 'none' },
        },
        {
          key: 'agentkey__activation_key',
          label: 'Agent Key',
          sortable: true,
          orderBy: 'agentkey__activation_key',
          headerTooltip: 'Cloud Agent Key at Qualys',
          tooltip: (rowData) => rowData['agentkey__activation_key'],
          headerCellStyle: { width: 'auto' },
          rowCellStyle: { width: 'auto', textDecoration: 'none' },
          enableCopyToClipboard: true,
        },
        {
          key: 'os',
          label: 'Operating System',
          enableCopyToClipboard: true,
          overrideContents: (rowData) => (
            <OSInfo id={rowData['id']} os={rowData['os']} />
          ),
          tooltip: (rowData) => rowData['os'],
          rowCellStyle: { textDecoration: 'none' },
          sortable: true,
          orderBy: 'os',
          headerTooltip: 'Host operating system',
        },
        {
          key: 'name_with_type',
          label: 'Host',
          sortable: true,
          orderBy: 'name',
          headerCellStyle: { width: 'auto' },
          rowCellStyle: { width: 'auto', textDecoration: 'none' },
          overrideContents: (rowData) => {
            const name = rowData['name'];

            return (
              <div>
                <div className="d-flex align-items-center">
                  <i
                    className={`fa fa-${
                      rowData['server_or_workstation'] === 'Workstations'
                        ? 'desktop'
                        : 'server'
                    }`}
                    style={{
                      color: '#0e74bc',
                      fontSize: '18px',
                      marginRight: '8px',
                      minWidth: '26px',
                    }}
                  ></i>
                  <div
                    style={{
                      textDecoration: 'underline',
                      textDecorationStyle: 'dotted',
                    }}
                  >
                    <strong>{truncateText(name, 45)}</strong>
                  </div>
                </div>
              </div>
            );
          },
          tooltip: (rowData) => {
            const alias = rowData['alias'];
            const name = rowData['name'];
            const hostType =
              rowData['server_or_workstation'] === 'Workstations'
                ? 'Workstation'
                : rowData['server_or_workstation'];

            return (
              <div>
                <strong>Host Type:</strong>&nbsp;&nbsp;
                <i
                  className={`fa fa-${
                    rowData['server_or_workstation'] === 'Workstations'
                      ? 'desktop'
                      : 'server'
                  }`}
                  style={{ color: '#0e74bc', fontSize: '18px' }}
                ></i>{' '}
                {hostType}
                <br />
                <strong>Host name:</strong> {name}
                {alias && (
                  <>
                    <br />
                    <strong>Alias:</strong> {alias}
                  </>
                )}
              </div>
            );
          },
          headerTooltip: 'Host system name',
        },
        {
          key: 'name_with_type_and_alias',
          label: 'Host Info',
          sortable: true,
          orderBy: 'name',
          rowCellStyle: {
            fontWeight: 'bold',
            width: 'auto',
            textDecoration: 'none',
          },
          headerCellStyle: { width: 'auto' },
          overrideContents: (rowData) => {
            const alias = rowData['alias'];
            const name = rowData['name'];
            const displayName = alias || name;

            return (
              <div>
                <div className="d-flex align-items-center">
                  <i
                    className={`fa fa-${
                      rowData['server_or_workstation'] === 'Workstations'
                        ? 'desktop'
                        : 'server'
                    }`}
                    style={{
                      color: '#0e74bc',
                      fontSize: '18px',
                      marginRight: '8px',
                      minWidth: '26px',
                    }}
                  ></i>
                  <div
                    style={{
                      textDecoration: 'underline',
                      textDecorationStyle: 'dotted',
                    }}
                  >
                    <strong>{truncateText(displayName, 45)}</strong>
                    {alias && (
                      <div className="small">{truncateText(name, 42)}</div>
                    )}
                  </div>
                </div>
              </div>
            );
          },
          tooltip: (rowData) => {
            const alias = rowData['alias'];
            const name = rowData['name'];
            const hostType =
              rowData['server_or_workstation'] === 'Workstations'
                ? 'Workstation'
                : rowData['server_or_workstation'];

            return (
              <div>
                <strong>Host Type:</strong>&nbsp;&nbsp;
                <i
                  className={`fa fa-${
                    rowData['server_or_workstation'] === 'Workstations'
                      ? 'desktop'
                      : 'server'
                  }`}
                  style={{ color: '#0e74bc', fontSize: '18px' }}
                ></i>{' '}
                {hostType}
                <br />
                <strong>Host name:</strong> {name}
                {alias && (
                  <>
                    <br />
                    <strong>Alias:</strong> {alias}
                  </>
                )}
              </div>
            );
          },
          headerTooltip:
            'Composite of Host Name, Type and Alias (primary if any alias)',
        },
        {
          key: 'alias',
          label: 'Alias',
          sortable: true,
          headerTooltip: 'Custom alias',
        },
        {
          key: 'address',
          label: 'Address',
          sortable: true,
          orderBy: 'big_ip',
          headerTooltip: 'Host system IPv4 address',
        },
        {
          key: 'server_or_workstation',
          label: 'Type',
          sortable: true,
          orderBy: 'server_or_workstation',
          render: (rowData) =>
            rowData['server_or_workstation'] === 'Workstations'
              ? 'Workstation'
              : rowData['server_or_workstation'],
          headerTooltip: 'Indicates if it is a Server or Workstation',
        },
        // NON-DELETED COLUMNS
        {
          key: 'total_vuln',
          label: 'Vuln. Count',
          sortable: true,
          headerTooltip: 'Total number of vulnerabilities',
          overrideContents: (rowData) => (
            <label
              className="label btn slim"
              style={{ color: 'rgb(255, 255, 255)', cursor: 'pointer' }}
            >
              {rowData.total_vuln}
            </label>
          ),
          onClick: (rowData) =>
            history.push(
              getDrillDownUrl({
                type: 'open',
                sourceType:
                  rowData['server_or_workstation'] === 'Server'
                    ? 'ca_data'
                    : 'ca_data-workstations',
                hosts_in: [rowData.id],
                severity: '',
                issueType: '',
                qid: '',
                severity_in: undefined,
              }),
            ),
        },
        {
          key: 'deleted_cloudagent__open_vuln_counts',
          label: 'Open Vuln. Count',
          sortable: true,
          headerTooltip: 'Number of open vulnerabilities',
          overrideContents: (rowData) => (
            <label
              className="label btn slim"
              style={{ color: 'rgb(255, 255, 255)', cursor: 'pointer' }}
            >
              {rowData.deleted_cloudagent__open_vuln_counts}
            </label>
          ),
        },
        {
          key: 'deleted_cloudagent__closed_vuln_counts',
          label: 'Closed Vuln. Count',
          sortable: true,
          headerTooltip: 'Number of closed vulnerabilities',
          overrideContents: (rowData) => (
            <label
              className="label btn slim"
              style={{ color: 'rgb(255, 255, 255)', cursor: 'pointer' }}
            >
              {rowData.deleted_cloudagent__closed_vuln_counts}
            </label>
          ),
        },
        {
          key: 'deleted_cloudagent__deleted_at',
          label: 'Deleted At',
          sortable: true,
          headerTooltip: 'Cloud Agent deletion date',
        },
        {
          key: 'deleted_cloudagent__deleted_by',
          label: 'Deleted By',
          sortable: true,
          headerTooltip: 'User who deleted the Cloud Agent',
        },
        {
          key: 'new_vuln',
          label: 'New Vuln.',
          sortable: true,
          headerTooltip: 'Number of new vulnerabilities',
          overrideContents: (rowData) => (
            <label
              className="label btn slim"
              style={{ color: 'rgb(255, 255, 255)' }}
            >
              {rowData.new_vuln}
            </label>
          ),
        },
        {
          key: 'ca_created',
          label: 'Created At',
          sortable: true,
          orderBy: 'ca_created',
          headerTooltip: 'Installation date of the cloud agent',
        },
        {
          key: 'last_vuln_scan',
          label: 'Last Scanned At',
          sortable: true,
          orderBy: 'last_vuln_scan',
          headerTooltip: 'Last scanned date',
        },
        {
          key: 'last_checked_in',
          label: 'Last Checked In',
          sortable: true,
          orderBy: 'last_checked_in',
          headerTooltip: 'Last checked in date',
          render: (rowData) => (
            <AgingOutLastDate
              lastDate={rowData['last_checked_in']}
              totalDays={1 * 30}
              dangerDaysDiff={10}
              warningDaysDiff={20}
              tooltipTitle="Cloud agent is inactive and will age out"
            />
          ),
        },
        {
          key: 'actions',
          label: 'Actions',
          render: (rowData) => <RowActions rowData={rowData} />,
          headerCellStyle: {
            textAlign: 'right',
            maxWidth: 'auto',
            minWidth: '150px !important',
          },
          rowCellStyle: {
            textAlign: 'right',
            minWidth: '150px !important',
            maxWidth: 'auto',
          },
        },
      ],
      columnsDisplayConfig: getColumnsDisplayConfig(),
      rowConfig: {
        classRules: [
          {
            className: 'warning-row',
            ruleFunction: isCloudAgentAgingOut,
          },
          {
            className: 'danger-row',
            ruleFunction: isDeletedFilterSelected,
          },
          {
            className: 'applied-row',
            ruleFunction: (rowData) =>
              rowData['isAppliedForDashboard'] === true,
          },
        ],
      },
      paginationConfig: {
        total_count: listingData?.count || 0,
        pageNumber: paginationConfig.page - 1,
        pageSize: paginationConfig.page_size,
        onPageChange: (newPage: number) => {
          setPaginationConfig({
            page_size: paginationConfig.page_size,
            page: newPage + 1,
          });
          if (!enableSelectionAcrossPages) {
            setSelectedRowIds([]);
          }
        },
        onPageSizeChange: (newPageSize: number) => {
          setPaginationConfig({ page_size: newPageSize, page: 1 });
          setSelectedRowIds([]);
        },
      },
      sortingConfig: {
        orderBy: orderBy,
        order: sortOrder,
        onOrderByChange: (value: string) => setOrderBy(value),
        onOrderChange: (value) => setSortOrder(value),
      },
    };

    // Conditionally add row selection configuration
    config.rowSelectionConfig = isDeletedFilterSelected()
      ? undefined
      : {
          enableSelectionAcrossPages: enableSelectionAcrossPages,
          preSelectedRows: selectedRowIds,
          onSelectionChange: (updatedRowIds) => {
            setSelectedRowIds(updatedRowIds);
          },
        };
    return config;
  };

  const config = useMemo<DataGridConfig>(() => {
    return getGridConfig();
  }, [
    listingData,
    activeCAColumnKeys,
    agedOutCAColumnKeys,
    forceRerenderUI,
    paginationConfig,
    selectedRowIds,
    appliedRowIds,
  ]);

  return {
    config,
    data: listingData?.items,
    loading,
  };
};
